/**
 * Page: Settings
 * Gestion des paramètres du compte utilisateur et des sessions actives
 */

import { initFullNameInput } from './utils/full-name-split.js';
import { initPhoneInputs } from '@/_includes/components/phone-input.js';
import { authContext } from '@/scripts/utils/auth-context.js';
import { KratosFlows } from '@/scripts/api/ory-client.js';
import { alert } from '@/scripts/utils/alert-message.js';
import { captureEvent, captureError } from '@/scripts/utils/logrocket.js';
import { extractKratosError } from '@/scripts/utils/kratos-errors.js';
import MicroModal from 'micromodal';

// Variable pour éviter les appels multiples simultanés
let isLoadingSessions = false;

document.addEventListener('DOMContentLoaded', async () => {
  try {
    // Initialiser authContext (il gère tout : OAuth, sessions, etc.)
    await authContext.init();

    // ============================================
    // PRÉCONDITION OAUTH COMPLETION (AVANT NETTOYAGE)
    // ============================================
    if (authContext.get('oauthLoginChallenge')) {
      console.log('[Settings] OAuth login challenge detected, redirecting to oauth-callback');
      window.location.href = '/oauth-callback.html';
      return;
    }

    // Vérifier l'authentification
    if (!authContext.isAuthenticated()) {
      console.log('[Settings] User not authenticated, redirecting to login');
      window.location.href = '/login.html';
      return;
    }

    // Nettoyer tout journey en cours
    if (authContext.getCurrentJourney()) {
      authContext.clearJourney();
    }

    // Initialiser Micromodal
    try {
      MicroModal.init({
        disableScroll: true,
        disableFocus: false
      });
      console.log('[Settings] Micromodal initialized successfully');

    } catch (error) {
      console.error('[Settings] Error initializing Micromodal:', error);
    }

    const session = authContext.getSession();

    // Initialiser les composants UI
    initFullNameInput();
    initPhoneInputs();

    // Remplir le formulaire avec les données utilisateur
    populateForm(session);

    // Configurer les handlers de déconnexion
    setupLogoutHandlers();

    // Configurer les handlers des modales
    setupModalHandlers();

    // Charger et afficher les sessions actives
    await loadActiveSessions();

    // Configurer les handlers de formulaires
    setupFormHandlers();

  } catch (error) {
    console.error('[Settings] Error during initialization:', error);
    captureError(error, { context: 'settings_init' });
  }
});

/**
 * Charge et affiche les sessions actives
 */
async function loadActiveSessions() {
  if (isLoadingSessions) {
    return;
  }

  isLoadingSessions = true;

  try {
    const allSessions = await KratosFlows.getAllSessions() || [];
    const currentSession = authContext.getSession();

    const sessionsContainer = document.querySelector('[data-sessions-container]');
    if (!sessionsContainer) {
      return;
    }

    // Vider le conteneur
    sessionsContainer.innerHTML = '';

    // listMySessions() retourne les AUTRES sessions (autres appareils)
    // La session actuelle vient de whoami, donc on doit toujours l'ajouter
    const sessionsList = [...allSessions];

    // Vérifier si la session actuelle est dans la liste
    const currentSessionInList = sessionsList.find(s => s.id === currentSession?.id);

    // Si la session actuelle n'est pas dans la liste, l'ajouter en premier
    if (currentSession && !currentSessionInList) {
      sessionsList.unshift(currentSession);
    }

    // Si aucune session à afficher (même pas la session actuelle), sortir
    if (sessionsList.length === 0) {
      return;
    }

    sessionsList.forEach((session) => {
      const isCurrentSession = session.id === currentSession?.id;
      const sessionElement = createSessionElement(session, isCurrentSession, currentSession?.id);
      sessionsContainer.appendChild(sessionElement);
    });

  } catch (error) {
    console.error('[Settings] Error loading active sessions:', error);
    captureError(error, { context: 'loadActiveSessions' });
  } finally {
    isLoadingSessions = false;
  }
}

/**
 * Crée un élément HTML pour une session
 */
function createSessionElement(session, isCurrentSession, currentSessionId) {
  const sessionDiv = document.createElement('div');
  sessionDiv.className = `flex items-start justify-between p-4 border rounded-lg ${isCurrentSession ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`;

  const deviceInfo = parseUserAgent(session.devices?.[0]?.user_agent || '');
  const location = 'Localisation inconnue'; // TODO: Implémenter la géolocalisation
  const lastActive = formatLastActive(session.authenticated_at);

  sessionDiv.innerHTML = `
    <div class="flex items-start gap-3">
      <div class="w-10 h-10 rounded-full ${isCurrentSession ? 'bg-green-100' : 'bg-gray-100'} flex items-center justify-center flex-shrink-0">
        <svg class="w-5 h-5 ${isCurrentSession ? 'text-green-600' : 'text-gray-600'}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          ${getDeviceIcon(deviceInfo.type)}
        </svg>
      </div>
      <div class="flex-1">
        <h3 class="font-medium text-gray-900">${deviceInfo.name}</h3>
        <p class="text-sm text-gray-500">${location} • ${lastActive}</p>
        <p class="text-xs text-gray-400 font-mono">ID: ${session.id.substring(0, 8)}...</p>
        ${isCurrentSession ? '<p class="text-xs text-green-600 mt-1">Session actuelle</p>' : ''}
        
        <!-- Boutons sur une nouvelle ligne -->
        <div class="flex gap-2 mt-3">
          ${isCurrentSession ? `
            <button type="button" data-logout-current class="text-red-600 hover:text-red-700 font-medium text-sm cursor-pointer px-3 py-1 border border-red-300 rounded hover:bg-red-50">
              Déconnecter
            </button>
          ` : `
            <button type="button" disabled class="text-gray-400 font-medium text-sm px-3 py-1 border border-gray-300 rounded bg-gray-100 cursor-not-allowed">
              Déconnecter
            </button>
          `}
          <button type="button" data-refresh-session="${session.id}" class="text-blue-600 hover:text-blue-700 font-medium text-sm cursor-pointer px-3 py-1 border border-blue-300 rounded hover:bg-blue-50">
            Actualiser
          </button>
        </div>
      </div>
    </div>
  `;

  return sessionDiv;
}

/**
 * Parse le user agent pour extraire les informations de l'appareil
 */
function parseUserAgent(userAgent) {
  if (!userAgent) {
    return { name: 'Appareil inconnu', type: 'unknown' };
  }

  const ua = userAgent.toLowerCase();

  if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
    return { name: 'Mobile', type: 'mobile' };
  } else if (ua.includes('tablet') || ua.includes('ipad')) {
    return { name: 'Tablette', type: 'tablet' };
  } else if (ua.includes('windows')) {
    return { name: 'Windows', type: 'desktop' };
  } else if (ua.includes('mac')) {
    return { name: 'Mac', type: 'desktop' };
  } else if (ua.includes('linux')) {
    return { name: 'Linux', type: 'desktop' };
  }

  return { name: 'Ordinateur', type: 'desktop' };
}

/**
 * Retourne l'icône SVG pour le type d'appareil
 */
function getDeviceIcon(type) {
  switch (type) {
    case 'mobile':
      return '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>';
    case 'tablet':
      return '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>';
    default:
      return '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>';
  }
}

/**
 * Formate la date de dernière activité
 */
function formatLastActive(authenticatedAt) {
  if (!authenticatedAt) return 'Inconnu';

  const date = new Date(authenticatedAt);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'À l\'instant';
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;

  return date.toLocaleDateString('fr-FR');
}

/**
 * Configure les handlers des modales
 */
function setupModalHandlers() {
  // Bouton de confirmation pour la session actuelle
  const logoutCurrentBtn = document.getElementById('logout-current-confirm-btn');
  if (logoutCurrentBtn) {
    logoutCurrentBtn.addEventListener('click', confirmLogoutCurrent);
  }

  // Bouton de confirmation pour toutes les sessions
  const logoutAllBtn = document.getElementById('logout-all-confirm-btn');
  if (logoutAllBtn) {
    logoutAllBtn.addEventListener('click', confirmLogoutAll);
  }

  // Bouton de confirmation pour une session spécifique
  const logoutDeviceBtn = document.getElementById('logout-confirm-btn');
  if (logoutDeviceBtn) {
    logoutDeviceBtn.addEventListener('click', confirmLogoutDevice);
  }
}

/**
 * Configure les handlers de déconnexion
 */
function setupLogoutHandlers() {
  // Bouton "Déconnecter tout"
  const logoutAllBtn = document.querySelector('[data-logout]');
  if (logoutAllBtn) {
    logoutAllBtn.addEventListener('click', handleLogoutAll);
  }

  // Event delegation pour les boutons de session
  document.addEventListener('click', (e) => {
    if (e.target.matches('[data-logout-device]')) {
      const deviceId = e.target.getAttribute('data-logout-device');
      handleLogoutDevice(deviceId);
    } else if (e.target.matches('[data-logout-current]')) {
      handleLogoutCurrent();
    } else if (e.target.matches('[data-refresh-session]')) {
      const sessionId = e.target.getAttribute('data-refresh-session');
      handleRefreshSession(sessionId);
    }

  });
}

/**
 * Gère la déconnexion de tous les appareils
 */
async function handleLogoutAll() {
  MicroModal.show('logout-all-confirm-modal');
}

/**
 * Gère la déconnexion de la session actuelle
 */
async function handleLogoutCurrent() {
  const modal = document.getElementById('logout-current-confirm-modal');
  if (!modal) return;

  try {
    MicroModal.show('logout-current-confirm-modal');
  } catch (error) {
    // Fallback: utiliser confirm() si la modal ne fonctionne pas
    if (confirm('Êtes-vous sûr de vouloir déconnecter cette session ?')) {
      confirmLogoutCurrent();
    }
  }
}

/**
 * Gère la déconnexion d'un device spécifique
 */
async function handleLogoutDevice(deviceId) {
  const messageEl = document.getElementById('logout-confirm-message');
  if (messageEl) {
    messageEl.textContent = `Êtes-vous sûr de vouloir déconnecter la session ${deviceId.substring(0, 8)}... ?`;
  }

  window.pendingLogoutSessionId = deviceId;

  try {
    MicroModal.show('logout-confirm-modal');
  } catch (error) {
    // Fallback: utiliser confirm() si la modal ne fonctionne pas
    if (confirm(`Êtes-vous sûr de vouloir déconnecter la session ${deviceId.substring(0, 8)}... ?`)) {
      confirmLogoutDevice();
    }
  }
}

/**
 * Gère l'actualisation d'une session
 */
async function handleRefreshSession(sessionId) {
  try {
    showSuccessMessage(`🔄 Actualisation de la session ${sessionId.substring(0, 8)}...`);

    await loadActiveSessions();

    setTimeout(() => {
      showSuccessMessage(`✅ Session ${sessionId.substring(0, 8)} actualisée !`);
    }, 500);

    captureEvent('session_refreshed', { sessionId });

  } catch (error) {
    console.error('[Settings] Error during session refresh:', error);
    captureError(error, { context: 'handleRefreshSession', sessionId });
  }
}

/**
 * Confirme la déconnexion de la session actuelle
 */
async function confirmLogoutCurrent() {
  try {
    // Fermer la modal d'abord
    MicroModal.close('logout-current-confirm-modal');

    // Utiliser le même flow que l'ancien settings
    await KratosFlows.logout();
    window.location.href = '/login.html';
  } catch (error) {
    console.error('[Settings] Error during current session logout:', error);
    captureError(error, { context: 'confirmLogoutCurrent' });
  }
}

/**
 * Confirme la déconnexion d'une session spécifique
 */
async function confirmLogoutDevice() {
  const sessionId = window.pendingLogoutSessionId;
  if (!sessionId) return;

  try {
    MicroModal.close('logout-confirm-modal');

    showSuccessMessage(`🔄 Déconnexion de la session ${sessionId.substring(0, 8)}...`);

    setTimeout(() => {
      window.location.reload();
    }, 1500);

    captureEvent('logout_device_confirmed', { sessionId });

  } catch (error) {
    console.error('[Settings] Error during device logout:', error);
    captureError(error, { context: 'confirmLogoutDevice', sessionId });
  }
}

/**
 * Confirme la déconnexion de tous les appareils
 */
async function confirmLogoutAll() {
  try {
    MicroModal.close('logout-all-confirm-modal');

    const currentSession = authContext.getSession();
    const allSessions = await KratosFlows.getAllSessions();

    if (!allSessions || allSessions.length === 0) {
      throw new Error('Aucune session trouvée');
    }

    const otherSessions = allSessions.filter(session => session.id !== currentSession?.id);

    if (otherSessions.length === 0) {
      alert.info('#alert-container', 'Aucune autre session active trouvée.', {
        icon: true,
        closable: true,
        duration: 3000
      });
      return;
    }

    showSuccessMessage(`🔄 Rechargement en cours pour déconnecter les autres sessions...`);

    setTimeout(() => {
      window.location.reload();
    }, 2000);

    captureEvent('logout_other_sessions_confirmed', {
      revokedCount: otherSessions.length
    });

  } catch (error) {
    console.error('[Settings] Error during logout all:', error);
    captureError(error, { context: 'confirmLogoutAll' });

    const errorMsg = extractKratosError(error, 'Erreur lors de la déconnexion. Veuillez réessayer.');
    alert.error('#alert-container', errorMsg, { icon: true, closable: true });
  }
}

/**
 * Remplit le formulaire avec les données de session
 */
function populateForm(session) {
  const traits = session.identity?.traits;

  if (!traits) return;

  // Nom complet - utiliser le champ full_name pour le split automatique
  const fullNameInput = document.querySelector('input[name="full_name"]');
  if (fullNameInput && traits.name) {
    const fullName = [traits.name.first, traits.name.last].filter(Boolean).join(' ');
    fullNameInput.value = fullName;
  }

  // Email
  const emailInput = document.querySelector('input[name="email"]');
  if (emailInput && traits.email) {
    emailInput.value = traits.email;
  }

  // Téléphone
  const phoneInput = document.querySelector('input[name="phone"]');
  if (phoneInput && traits.phone) {
    phoneInput.value = traits.phone;
  }
}

/**
 * Configure les handlers de formulaires
 */
function setupFormHandlers() {
  // Gestion du formulaire de profil
  const profileForm = document.getElementById('profile-form');
  if (profileForm) {
    profileForm.addEventListener('submit', async (e) => {
      e.preventDefault();

      try {
        // Créer un flow settings
        const settingsFlow = await KratosFlows.initSettingsFlow();
        const flowId = settingsFlow.id;

        const formData = new FormData(profileForm);
        const fullName = formData.get('full_name');
        const email = formData.get('email');
        const phone = formData.get('phone');

        // Split du nom complet en first/last
        const nameParts = fullName.trim().split(/\s+/);
        const firstName = nameParts[0];
        const lastName = nameParts.slice(1).join(' ') || null;

        // Construire le payload traits
        const traits = {
          email: email,
          name: {
            first: firstName,
            last: lastName,
          },
        };

        // Ne pas inclure le champ phone s'il est vide
        if (phone && phone.trim()) {
          traits.phone = phone;
        }

        // Récupérer le flow actuel pour obtenir le token CSRF
        const currentFlow = await KratosFlows.getFlow(flowId, 'settings');
        const csrfToken = currentFlow.ui.nodes.find(n => n.attributes.name === 'csrf_token')?.attributes.value;

        // Soumettre les modifications
        await KratosFlows.submitFlow(flowId, 'settings', {
          method: 'profile',
          csrf_token: csrfToken,
          traits
        });

        // Mettre à jour la session locale
        await authContext.refreshSession();

        alert.success('#alert-container', 'Profil mis à jour avec succès !', {
          icon: true,
          closable: true,
          duration: 3000
        });

        // Recharger la page pour afficher les nouvelles données
        setTimeout(() => {
          window.location.reload();
        }, 1500);

      } catch (error) {
        console.error('[Settings] Error updating profile:', error);
        captureError(error, { context: 'profile_update' });

        const errorMsg = extractKratosError(error, 'Erreur lors de la mise à jour du profil. Veuillez réessayer.');
        alert.error('#alert-container', errorMsg, { icon: true, closable: true });
      }
    });
  }

  // Gestion du formulaire de changement de mot de passe
  const passwordForm = document.getElementById('password-form');
  if (passwordForm) {
    passwordForm.addEventListener('submit', async (e) => {
      e.preventDefault();

      try {
        // Créer un flow settings
        const settingsFlow = await KratosFlows.initSettingsFlow();
        const flowId = settingsFlow.id;

        const formData = new FormData(passwordForm);
        const password = formData.get('password');
        const confirmPassword = formData.get('confirmPassword');

        if (password !== confirmPassword) {
          alert.error('#alert-container', 'Les mots de passe ne correspondent pas.', {
            icon: true,
            closable: true
          });
          return;
        }

        // Récupérer le flow actuel pour obtenir le token CSRF
        const currentFlow = await KratosFlows.getFlow(flowId, 'settings');
        const csrfToken = currentFlow.ui.nodes.find(n => n.attributes.name === 'csrf_token')?.attributes.value;

        // Soumettre le changement de mot de passe
        await KratosFlows.submitFlow(flowId, 'settings', {
          method: 'password',
          csrf_token: csrfToken,
          password
        });

        alert.success('#alert-container', 'Mot de passe modifié avec succès !', {
          icon: true,
          closable: true,
          duration: 3000
        });

        // Vider le formulaire
        passwordForm.reset();

      } catch (error) {
        console.error('[Settings] Error changing password:', error);
        captureError(error, { context: 'password_change' });

        const errorMsg = extractKratosError(error, 'Erreur lors du changement de mot de passe. Veuillez réessayer.');
        alert.error('#alert-container', errorMsg, { icon: true, closable: true });
      }
    });
  }
}

/**
 * Affiche un message de succès animé
 */
function showSuccessMessage(message) {
  const toast = document.createElement('div');
  toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 transform transition-all duration-300 ease-in-out';
  toast.style.transform = 'translateX(100%)';
  toast.innerHTML = `
    <div class="flex items-center gap-2">
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>
      <span>${message}</span>
    </div>
  `;

  document.body.appendChild(toast);

  // Animation d'entrée
  setTimeout(() => {
    toast.style.transform = 'translateX(0)';
  }, 100);

  // Animation de sortie
  setTimeout(() => {
    toast.style.transform = 'translateX(100%)';
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 300);
  }, 3000);
}